import { useState } from "react";
import { useSearchParams } from "react-router-dom";

import {
    createGlobalErrorMessage,
    FileMimeType,
    url,
    useDeleteSpecialPriceMutation,
    useDeleteSpecialPricesMutation,
    useDownloadFileMutation,
} from "src/api";
import { SpecialPriceForm } from "src/app/customers/containers";
import { Button, Modal, TableWithButtonsLayout } from "src/components";
import { useModal } from "src/modal";
import { NotificationTypes, useMutationNotification, useNotifications } from "src/notifications";
import { createFiltersSearchParam, TableNames } from "src/table";
import { useTranslation } from "src/translations";
import type { RequestErrorType } from "src/types";
import { formatDateForBE, SPECIAL_PRICES_FORM_MODAL_WIDTH, useAppDispatch } from "src/utils";
import { DashboardCard } from "../DashboardCard";
import { ALL_SPECIAL_PRICES_FORM_MODAL } from "./constants";
import { tableModule } from "./table";
import { createActionsForSelection, createSpecialPriceActionRow, overrideSpecialPriceRowProps } from "./utils";

export const AllSpecialPricesWidget = () => {
    const { t } = useTranslation();

    const [isFormModalOpened, openFormModal, closeFormModal] = useModal(ALL_SPECIAL_PRICES_FORM_MODAL);

    const [specialPriceIdForEdit, setSpecialPriceIdForEdit] = useState<number | undefined>(undefined);
    const [bulkIdForEdit, setBulkIdForEdit] = useState<number | undefined>(undefined);
    const [customerIdForEdit, setCustomerIdForEdit] = useState<number | undefined>(undefined);

    const [deleteSpecialPrice, deleteSpecialPriceResult] = useDeleteSpecialPriceMutation();
    const [bulkDelete] = useDeleteSpecialPricesMutation();

    const [downloadFile] = useDownloadFileMutation();
    const [searchParams] = useSearchParams();

    const notifications = useNotifications();
    const dispatch = useAppDispatch();

    useMutationNotification(deleteSpecialPriceResult, "specialPrices.delete.success", "specialPrices.delete.error");

    const editSpecialPrice = (specialPriceId: number, customerId: number | undefined, bulkId: number | undefined) => {
        setSpecialPriceIdForEdit(specialPriceId);
        setCustomerIdForEdit(customerId);
        setBulkIdForEdit(bulkId);
        openFormModal();
    };

    const onBulk = (ids: number[]) => {
        bulkDelete({ ids })
            .unwrap()
            .then(() => {
                dispatch(tableModule.resetTableSelectionAction);
                notifications.showNotification({
                    title: t("common.form.successTitle"),
                    message: t("dashboard.allSpecialPrices.bulkDelete.success", { count: ids.length }),
                    type: NotificationTypes.success,
                });
            })
            .catch((e) => {
                const error = e as RequestErrorType;
                const errorMessage = createGlobalErrorMessage(
                    error,
                    t("dashboard.allSpecialPrices.bulkDelete.error", { count: ids.length }),
                );
                notifications.showNotification({
                    title: t("common.form.errorTitle"),
                    message: errorMessage,
                    type: NotificationTypes.error,
                });
            });
    };

    return (
        <DashboardCard>
            <TableWithButtonsLayout
                buttons={
                    <Button
                        type="button"
                        onClick={() =>
                            downloadFile({
                                url: `${url.SPECIAL_PRICES_REPORT}?${
                                    searchParams.get(createFiltersSearchParam(TableNames.allSpecialPrices)) ?? ""
                                }`,
                                filename: `SPECIAL_PRICES_REPORT_${formatDateForBE(new Date())}`,
                                fileMimeType: FileMimeType.Xls,
                            })
                        }
                    >
                        {t("specialPrices.generateReport")}
                    </Button>
                }
                table={
                    <tableModule.Container
                        canSelectAll={false}
                        actions={createSpecialPriceActionRow(t, editSpecialPrice, deleteSpecialPrice)}
                        actionsForSelection={createActionsForSelection(t, onBulk)}
                        isSelectable
                        overrideRowProps={overrideSpecialPriceRowProps}
                    />
                }
            />
            <Modal
                title={bulkIdForEdit ? t("specialPrices.modal.bulkUpdateTitle") : t("specialPrices.modal.updateTitle")}
                opened={isFormModalOpened}
                onClose={closeFormModal}
                maxWidth={SPECIAL_PRICES_FORM_MODAL_WIDTH}
            >
                <SpecialPriceForm
                    customerId={customerIdForEdit?.toString() ?? null}
                    closeModal={closeFormModal}
                    specialPriceId={specialPriceIdForEdit}
                />
            </Modal>
        </DashboardCard>
    );
};
